export const localeCodes = ["en", "kr", "zh", "fr", "es", "ar", "ru", "tr", "fa", "hi", "sn", "id"] as const;

export type LocaleCode = (typeof localeCodes)[number];

export const defaultLocale: LocaleCode = "en";

export const officialWorldLanguageCodes = ["en", "zh", "fr", "es", "ar", "ru"] as const satisfies readonly LocaleCode[];

export type InstrumentId =
  | "gayageum"
  | "erhu"
  | "oud"
  | "sitar"
  | "daegeum"
  | "ney"
  | "janggu"
  | "tabla"
  | "mbira"
  | "kora"
  | "yidaki_didgeridoo"
  | "gamelan_saron";

export interface LocaleConfig {
  code: LocaleCode;
  htmlLang: string;
  dir: "ltr" | "rtl";
  path: string;
  nativeName: string;
  englishName: string;
  regionLabel: string;
  focusDescription: string;
  featuredInstrumentIds: InstrumentId[];
  isOfficialWorldLanguage: boolean;
}

export const instrumentLocaleMap: Record<InstrumentId, LocaleCode[]> = {
  gayageum: ["kr"],
  erhu: ["zh"],
  oud: ["ar", "tr"],
  sitar: ["hi"],
  daegeum: ["kr"],
  ney: ["ar", "tr", "fa"],
  janggu: ["kr"],
  tabla: ["hi"],
  mbira: ["sn"],
  kora: ["fr", "en"],
  yidaki_didgeridoo: ["en"],
  gamelan_saron: ["id"]
};

export const localeConfigs: Record<LocaleCode, LocaleConfig> = {
  en: {
    code: "en",
    htmlLang: "en",
    dir: "ltr",
    path: "/",
    nativeName: "English",
    englishName: "English",
    regionLabel: "Global English / Australia / West Africa",
    focusDescription:
      "Default English experience, including instruments linked to Australian and English-language regional contexts.",
    featuredInstrumentIds: ["yidaki_didgeridoo", "kora"],
    isOfficialWorldLanguage: true
  },
  kr: {
    code: "kr",
    htmlLang: "ko-KR",
    dir: "ltr",
    path: "/kr",
    nativeName: "한국어",
    englishName: "Korean",
    regionLabel: "Korea",
    focusDescription: "가야금, 대금, 장구처럼 한국 음악 문화권과 직접 연결된 악기를 중심으로 보여줍니다.",
    featuredInstrumentIds: ["gayageum", "daegeum", "janggu"],
    isOfficialWorldLanguage: false
  },
  zh: {
    code: "zh",
    htmlLang: "zh-Hans",
    dir: "ltr",
    path: "/zh",
    nativeName: "中文",
    englishName: "Chinese",
    regionLabel: "China",
    focusDescription: "面向中文页面，突出二胡以及与中国音乐实践相关的材料和演奏语境。",
    featuredInstrumentIds: ["erhu"],
    isOfficialWorldLanguage: true
  },
  fr: {
    code: "fr",
    htmlLang: "fr",
    dir: "ltr",
    path: "/fr",
    nativeName: "Français",
    englishName: "French",
    regionLabel: "West Africa / Francophone regions",
    focusDescription:
      "Page française pour les contextes francophones et les lignées de kora en Afrique de l'Ouest.",
    featuredInstrumentIds: ["kora"],
    isOfficialWorldLanguage: true
  },
  es: {
    code: "es",
    htmlLang: "es",
    dir: "ltr",
    path: "/es",
    nativeName: "Español",
    englishName: "Spanish",
    regionLabel: "Official world language",
    focusDescription:
      "Experiencia en español para explorar el catálogo mundial de instrumentos tradicionales.",
    featuredInstrumentIds: [],
    isOfficialWorldLanguage: true
  },
  ar: {
    code: "ar",
    htmlLang: "ar",
    dir: "rtl",
    path: "/ar",
    nativeName: "العربية",
    englishName: "Arabic",
    regionLabel: "Middle East / North Africa",
    focusDescription: "صفحة عربية تبرز العود والناي وسياقات المقام والأنفاس في المنطقة.",
    featuredInstrumentIds: ["oud", "ney"],
    isOfficialWorldLanguage: true
  },
  ru: {
    code: "ru",
    htmlLang: "ru",
    dir: "ltr",
    path: "/ru",
    nativeName: "Русский",
    englishName: "Russian",
    regionLabel: "Official world language",
    focusDescription:
      "Русская страница для просмотра мирового каталога традиционных инструментов.",
    featuredInstrumentIds: [],
    isOfficialWorldLanguage: true
  },
  tr: {
    code: "tr",
    htmlLang: "tr",
    dir: "ltr",
    path: "/tr",
    nativeName: "Türkçe",
    englishName: "Turkish",
    regionLabel: "Turkey / Ottoman and regional practice",
    focusDescription: "Ud ve ney gibi Türkiye ve komşu müzik pratikleriyle ilişkili çalgıları öne çıkarır.",
    featuredInstrumentIds: ["oud", "ney"],
    isOfficialWorldLanguage: false
  },
  fa: {
    code: "fa",
    htmlLang: "fa",
    dir: "rtl",
    path: "/fa",
    nativeName: "فارسی",
    englishName: "Persian",
    regionLabel: "Persian musical practice",
    focusDescription: "صفحه فارسی برای نی و زمینه های اجرایی مرتبط با موسیقی ایرانی.",
    featuredInstrumentIds: ["ney"],
    isOfficialWorldLanguage: false
  },
  hi: {
    code: "hi",
    htmlLang: "hi",
    dir: "ltr",
    path: "/hi",
    nativeName: "हिन्दी",
    englishName: "Hindi",
    regionLabel: "North India / South Asia",
    focusDescription: "सितार और तबला जैसे दक्षिण एशियाई व उत्तर भारतीय परंपराओं से जुड़े वाद्यों पर केंद्रित पृष्ठ।",
    featuredInstrumentIds: ["sitar", "tabla"],
    isOfficialWorldLanguage: false
  },
  sn: {
    code: "sn",
    htmlLang: "sn",
    dir: "ltr",
    path: "/sn",
    nativeName: "ChiShona",
    englishName: "Shona",
    regionLabel: "Zimbabwe / Shona contexts",
    focusDescription: "Peji reChiShona rinoratidza mbira nemamiriro ayo emimhanzi yeZimbabwe.",
    featuredInstrumentIds: ["mbira"],
    isOfficialWorldLanguage: false
  },
  id: {
    code: "id",
    htmlLang: "id",
    dir: "ltr",
    path: "/id",
    nativeName: "Bahasa Indonesia",
    englishName: "Indonesian",
    regionLabel: "Indonesia",
    focusDescription: "Halaman Bahasa Indonesia untuk gamelan saron dan konteks laras ansambel gamelan.",
    featuredInstrumentIds: ["gamelan_saron"],
    isOfficialWorldLanguage: false
  }
};

export interface UiText {
  brandTagline: string;
  nav: {
    play: string;
    instruments: string;
    about: string;
    qa: string;
    audioLab: string;
  };
  audio: {
    enable: string;
    ready: string;
    retry: string;
    unavailable: string;
    locked: string;
    statusReady: string;
    statusLocked: string;
  };
  controls: {
    instrument: string;
    variant: string;
    tuning: string;
    language: string;
    approxTuning: string;
    reviewedTuning: string;
  };
  pages: {
    instrumentsEyebrow: string;
    instrumentsTitle: string;
    origin: string;
    family: string;
    openInstrument: string;
    aboutEyebrow: string;
    aboutTitle: string;
    aboutBody: string[];
    qaEyebrow: string;
    qaTitle: string;
    localeFocus: string;
    connectedInstruments: string;
    officialLanguage: string;
  };
  play: {
    active: string;
    ready: string;
    quickStart: string;
    steps: string[];
    dismissQuickStart: string;
    audioLocked: string;
    activateAudioFromStage: string;
    mobileDrawerTitle: string;
    orientationActive: string;
    orientationRecommended: string;
  };
  soundDna: {
    eyebrow: string;
    title: string;
    compactLabel: string;
    controlsLabel: string;
    detailsHeading: string;
    variantInfo: string;
    variant: string;
    construction: string;
    controls: string;
    samples: string;
    samplePending: string;
    sampleAvailable: string;
    tuningWarning: string;
    tuning: string;
    respectNote: string;
    culturalContext: string;
    family: string;
    attack: string;
    sustain: string;
    resonance: string;
    ornament: string;
    identity: string;
    approximationFallback: string;
  };
  keyboard: {
    shift: string;
    space: string;
    arrows: string;
    numbers: string;
  };
  transport: {
    bend: string;
    vibrato: string;
    drone: string;
    loop: string;
    metro: string;
  };
  seo: {
    title: string;
    description: string;
  };
}

const enText: UiText = {
  brandTagline: "world instrument playground",
  nav: {
    play: "Play",
    instruments: "Instruments",
    about: "About",
    qa: "QA",
    audioLab: "Audio Lab"
  },
  audio: {
    enable: "Enable audio",
    ready: "Audio ready",
    retry: "Retry audio",
    unavailable: "Audio unavailable",
    locked: "Locked",
    statusReady: "Ready",
    statusLocked: "Locked"
  },
  controls: {
    instrument: "Instrument",
    variant: "Variant",
    tuning: "Tuning",
    language: "Language",
    approxTuning: "Approx tuning",
    reviewedTuning: "Reviewed tuning"
  },
  pages: {
    instrumentsEyebrow: "Playable catalog",
    instrumentsTitle: "World Instrument Catalog",
    origin: "Origin",
    family: "Family",
    openInstrument: "Open instrument",
    aboutEyebrow: "Product intent",
    aboutTitle: "A Playable World Instrument Playground",
    aboutBody: [
      "Timbroam is a browser-based traditional instrument platform. It is designed as a playable research surface, where tuning, variants, articulation, sound identity, and cultural context become part of the interaction rather than a static description.",
      "The current audio engine runs offline with no server or paid API. It uses browser-safe physical and spectral approximation models rather than licensed samples, and all tuning remains marked as source-review pending."
    ],
    qaEyebrow: "Verification",
    qaTitle: "UI, Audio, and Deployment QA",
    localeFocus: "Language page",
    connectedInstruments: "Connected instruments",
    officialLanguage: "Official world language page"
  },
  play: {
    active: "active",
    ready: "ready",
    quickStart: "Quick start",
    steps: ["Enable audio", "Choose an instrument", "Click, drag, or use your keyboard to play"],
    dismissQuickStart: "Dismiss quick start",
    audioLocked: "Audio locked",
    activateAudioFromStage: "Activate audio engine from performance stage",
    mobileDrawerTitle: "Sound DNA, cultural notes, and keys",
    orientationActive: "active",
    orientationRecommended: "recommended"
  },
  soundDna: {
    eyebrow: "Insight drawer",
    title: "Sound DNA",
    compactLabel: "Compact sound identity",
    controlsLabel: "Jump to linked performance controls",
    detailsHeading: "Family / articulation / resonance",
    variantInfo: "Variant info",
    variant: "Variant",
    construction: "Construction",
    controls: "Controls",
    samples: "Samples",
    samplePending: "No licensed samples loaded; advanced approximation engine active",
    sampleAvailable: "Licensed sample layers available",
    tuningWarning: "Tuning warning",
    tuning: "Tuning",
    respectNote: "Respect note",
    culturalContext: "Cultural context",
    family: "Family",
    attack: "Attack",
    sustain: "Sustain",
    resonance: "Resonance",
    ornament: "Ornament",
    identity: "Identity",
    approximationFallback: "Approximation requires source review."
  },
  keyboard: {
    shift: "Shift = ornament/accent",
    space: "Space = sustain/drone/hold",
    arrows: "Arrows = bend/intensity",
    numbers: "1-4 = articulation mode"
  },
  transport: {
    bend: "Bend",
    vibrato: "Vibrato",
    drone: "Drone",
    loop: "Loop",
    metro: "Metro"
  },
  seo: {
    title: "Timbroam",
    description: "Play and explore traditional instruments, tunings, variants, and cultural context in the browser."
  }
};

export const uiTextByLocale: Record<LocaleCode, UiText> = {
  en: enText,
  kr: {
    ...enText,
    brandTagline: "세계 전통 악기 플레이그라운드",
    nav: { play: "연주", instruments: "악기", about: "소개", qa: "QA", audioLab: "오디오 랩" },
    audio: {
      enable: "오디오 켜기",
      ready: "오디오 준비됨",
      retry: "오디오 다시 시도",
      unavailable: "오디오 사용 불가",
      locked: "잠김",
      statusReady: "준비됨",
      statusLocked: "잠김"
    },
    controls: { instrument: "악기", variant: "유형", tuning: "조율", language: "언어", approxTuning: "근사 조율", reviewedTuning: "검토된 조율" },
    pages: {
      ...enText.pages,
      instrumentsEyebrow: "연주 가능한 카탈로그",
      instrumentsTitle: "세계 악기 카탈로그",
      origin: "출신",
      family: "분류",
      openInstrument: "악기 열기",
      aboutEyebrow: "제품 의도",
      aboutTitle: "연주 가능한 세계 전통 악기 실험실",
      aboutBody: [
        "Timbroam은 브라우저에서 전통 악기를 연주하고 탐색하는 플랫폼입니다. 조율, 유형, 주법, 소리의 정체성, 문화적 맥락이 정적인 설명이 아니라 상호작용의 일부가 되도록 설계되었습니다.",
        "현재 단계는 Cloudflare Pages 배포에 맞는 정적 Vite, React, TypeScript, Web Audio, 라우팅, 테스트, 문서 구조를 마련합니다."
      ],
      qaEyebrow: "검증",
      qaTitle: "UI, 오디오, 배포 QA",
      localeFocus: "언어 페이지",
      connectedInstruments: "연결된 악기",
      officialLanguage: "세계 공용어 페이지"
    },
    play: {
      ...enText.play,
      active: "활성",
      ready: "준비",
      quickStart: "빠른 시작",
      steps: ["오디오 켜기", "악기 선택", "클릭, 드래그 또는 키보드로 연주"],
      dismissQuickStart: "빠른 시작 닫기",
      audioLocked: "오디오 잠김",
      activateAudioFromStage: "연주 화면에서 오디오 켜기",
      mobileDrawerTitle: "사운드 DNA, 문화 노트, 키",
      orientationActive: "활성",
      orientationRecommended: "권장"
    },
    soundDna: {
      ...enText.soundDna,
      eyebrow: "인사이트",
      title: "사운드 DNA",
      detailsHeading: "분류 / 주법 / 공명",
      variantInfo: "유형 정보",
      variant: "유형",
      construction: "구조",
      controls: "컨트롤",
      samples: "샘플",
      samplePending: "샘플 라이브러리 준비 중; 합성 대체음 사용",
      tuningWarning: "조율 경고",
      tuning: "조율",
      respectNote: "존중 노트",
      culturalContext: "문화 맥락",
      family: "분류",
      attack: "어택",
      sustain: "지속",
      resonance: "공명",
      ornament: "꾸밈음",
      identity: "정체성",
      approximationFallback: "근사값은 출처 검토가 필요합니다."
    },
    keyboard: { shift: "Shift = 꾸밈/악센트", space: "Space = 지속/드론/홀드", arrows: "화살표 = 굽힘/세기", numbers: "1-4 = 주법 모드" },
    transport: { bend: "벤드", vibrato: "비브라토", drone: "드론", loop: "루프", metro: "메트로" },
    seo: { title: "Timbroam 한국어", description: "브라우저에서 전통 악기의 조율, 유형, 문화 맥락을 연주하며 탐색합니다." }
  },
  zh: {
    ...enText,
    brandTagline: "世界乐器游乐场",
    nav: { play: "演奏", instruments: "乐器", about: "关于", qa: "QA", audioLab: "音频实验室" },
    controls: { instrument: "乐器", variant: "变体", tuning: "调弦", language: "语言", approxTuning: "近似调弦", reviewedTuning: "已审核调弦" },
    pages: { ...enText.pages, instrumentsEyebrow: "可演奏目录", instrumentsTitle: "世界乐器目录", origin: "来源", family: "类别", openInstrument: "打开乐器", localeFocus: "语言页面", connectedInstruments: "关联乐器", officialLanguage: "世界官方语言页面" },
    play: { ...enText.play, active: "活动", ready: "就绪", quickStart: "快速开始", steps: ["开启音频", "选择乐器", "点击、拖动或用键盘演奏"], audioLocked: "音频已锁定", mobileDrawerTitle: "声音 DNA、文化注记和按键" },
    soundDna: { ...enText.soundDna, eyebrow: "洞察", title: "声音 DNA", detailsHeading: "类别 / 技法 / 共鸣", variantInfo: "变体信息", construction: "构造", controls: "控制", samples: "采样", tuningWarning: "调弦提示", culturalContext: "文化语境", respectNote: "尊重提示", family: "类别", attack: "起音", sustain: "延音", resonance: "共鸣", ornament: "装饰音", identity: "身份" },
    seo: { title: "Timbroam 中文", description: "在浏览器中演奏并探索传统乐器、调弦、变体和文化语境。" }
  },
  fr: {
    ...enText,
    brandTagline: "terrain de jeu des instruments du monde",
    nav: { play: "Jouer", instruments: "Instruments", about: "À propos", qa: "QA", audioLab: "Audio Lab" },
    controls: { instrument: "Instrument", variant: "Variante", tuning: "Accord", language: "Langue", approxTuning: "Accord approx.", reviewedTuning: "Accord validé" },
    pages: { ...enText.pages, instrumentsEyebrow: "Catalogue jouable", instrumentsTitle: "Catalogue mondial d'instruments", origin: "Origine", family: "Famille", openInstrument: "Ouvrir l'instrument", localeFocus: "Page linguistique", connectedInstruments: "Instruments liés", officialLanguage: "Page de langue mondiale officielle" },
    play: { ...enText.play, active: "actif", ready: "prêt", quickStart: "Démarrage rapide", steps: ["Activer l'audio", "Choisir un instrument", "Cliquer, glisser ou jouer au clavier"], audioLocked: "Audio verrouillé", mobileDrawerTitle: "ADN sonore, notes culturelles et touches" },
    soundDna: { ...enText.soundDna, eyebrow: "Analyse", title: "ADN sonore", detailsHeading: "Famille / articulation / résonance", variantInfo: "Info variante", construction: "Construction", controls: "Contrôles", samples: "Échantillons", tuningWarning: "Avertissement d'accord", culturalContext: "Contexte culturel", respectNote: "Note de respect", family: "Famille", attack: "Attaque", sustain: "Tenue", resonance: "Résonance", ornament: "Ornement", identity: "Identité" },
    seo: { title: "Timbroam Français", description: "Jouez et explorez des instruments traditionnels, leurs accords, variantes et contextes culturels." }
  },
  es: {
    ...enText,
    brandTagline: "laboratorio lúdico de instrumentos del mundo",
    nav: { play: "Tocar", instruments: "Instrumentos", about: "Acerca de", qa: "QA", audioLab: "Audio Lab" },
    controls: { instrument: "Instrumento", variant: "Variante", tuning: "Afinación", language: "Idioma", approxTuning: "Afinación aprox.", reviewedTuning: "Afinación revisada" },
    pages: { ...enText.pages, instrumentsEyebrow: "Catálogo tocable", instrumentsTitle: "Catálogo mundial de instrumentos", origin: "Origen", family: "Familia", openInstrument: "Abrir instrumento", localeFocus: "Página de idioma", connectedInstruments: "Instrumentos vinculados", officialLanguage: "Página de idioma mundial oficial" },
    play: { ...enText.play, active: "activo", ready: "listo", quickStart: "Inicio rápido", steps: ["Activar audio", "Elegir un instrumento", "Haz clic, arrastra o usa el teclado"], audioLocked: "Audio bloqueado", mobileDrawerTitle: "ADN sonoro, notas culturales y teclas" },
    soundDna: { ...enText.soundDna, eyebrow: "Panel de análisis", title: "ADN sonoro", detailsHeading: "Familia / articulación / resonancia", variantInfo: "Información de variante", construction: "Construcción", controls: "Controles", samples: "Muestras", tuningWarning: "Aviso de afinación", culturalContext: "Contexto cultural", respectNote: "Nota de respeto", family: "Familia", attack: "Ataque", sustain: "Sostenido", resonance: "Resonancia", ornament: "Ornamento", identity: "Identidad" },
    seo: { title: "Timbroam Español", description: "Toca y explora instrumentos tradicionales, afinaciones, variantes y contexto cultural." }
  },
  ar: {
    ...enText,
    brandTagline: "مساحة عزف لآلات العالم",
    nav: { play: "اعزف", instruments: "الآلات", about: "حول", qa: "QA", audioLab: "مختبر الصوت" },
    controls: { instrument: "الآلة", variant: "النسخة", tuning: "الدوزان", language: "اللغة", approxTuning: "دوزان تقريبي", reviewedTuning: "دوزان موثق" },
    pages: { ...enText.pages, instrumentsEyebrow: "فهرس قابل للعزف", instrumentsTitle: "فهرس آلات العالم", origin: "الأصل", family: "العائلة", openInstrument: "افتح الآلة", localeFocus: "صفحة اللغة", connectedInstruments: "آلات مرتبطة", officialLanguage: "صفحة لغة عالمية رسمية" },
    play: { ...enText.play, active: "نشط", ready: "جاهز", quickStart: "بدء سريع", steps: ["فعّل الصوت", "اختر آلة", "انقر أو اسحب أو استخدم لوحة المفاتيح"], audioLocked: "الصوت مقفل", mobileDrawerTitle: "بصمة الصوت والملاحظات الثقافية والمفاتيح" },
    soundDna: { ...enText.soundDna, eyebrow: "تحليل", title: "بصمة الصوت", detailsHeading: "العائلة / التقنية / الرنين", variantInfo: "معلومات النسخة", variant: "النسخة", construction: "البناء", controls: "التحكم", samples: "العينات", tuningWarning: "تنبيه الدوزان", tuning: "الدوزان", respectNote: "ملاحظة احترام", culturalContext: "السياق الثقافي", family: "العائلة", attack: "البداية", sustain: "الاستمرار", resonance: "الرنين", ornament: "الزخرفة", identity: "الهوية" },
    seo: { title: "Timbroam العربية", description: "اعزف واستكشف آلات تقليدية ودوزاناتها ونسخها وسياقاتها الثقافية في المتصفح." }
  },
  ru: {
    ...enText,
    brandTagline: "игровая среда инструментов мира",
    nav: { play: "Играть", instruments: "Инструменты", about: "О проекте", qa: "QA", audioLab: "Аудио Lab" },
    controls: { instrument: "Инструмент", variant: "Вариант", tuning: "Строй", language: "Язык", approxTuning: "Прибл. строй", reviewedTuning: "Проверенный строй" },
    pages: { ...enText.pages, instrumentsEyebrow: "Игровой каталог", instrumentsTitle: "Мировой каталог инструментов", origin: "Происхождение", family: "Семейство", openInstrument: "Открыть инструмент", localeFocus: "Языковая страница", connectedInstruments: "Связанные инструменты", officialLanguage: "Страница официального мирового языка" },
    play: { ...enText.play, active: "активно", ready: "готово", quickStart: "Быстрый старт", steps: ["Включите аудио", "Выберите инструмент", "Играйте кликом, жестом или клавиатурой"], audioLocked: "Аудио заблокировано", mobileDrawerTitle: "Звуковая ДНК, культурные заметки и клавиши" },
    soundDna: { ...enText.soundDna, eyebrow: "Аналитика", title: "Звуковая ДНК", detailsHeading: "Семейство / артикуляция / резонанс", variantInfo: "Вариант", construction: "Конструкция", controls: "Управление", samples: "Сэмплы", tuningWarning: "Предупреждение о строе", culturalContext: "Культурный контекст", respectNote: "Заметка уважения", family: "Семейство", attack: "Атака", sustain: "Сустейн", resonance: "Резонанс", ornament: "Орнамент", identity: "Идентичность" },
    seo: { title: "Timbroam Русский", description: "Играйте и изучайте традиционные инструменты, строи, варианты и культурный контекст." }
  },
  tr: {
    ...enText,
    brandTagline: "dünya çalgıları oyun alanı",
    nav: { play: "Çal", instruments: "Çalgılar", about: "Hakkında", qa: "QA", audioLab: "Ses Lab" },
    controls: { instrument: "Çalgı", variant: "Varyant", tuning: "Akort", language: "Dil", approxTuning: "Yaklaşık akort", reviewedTuning: "İncelenmiş akort" },
    pages: { ...enText.pages, instrumentsEyebrow: "Çalınabilir katalog", instrumentsTitle: "Dünya Çalgıları Kataloğu", origin: "Köken", family: "Aile", openInstrument: "Çalgıyı aç", localeFocus: "Dil sayfası", connectedInstruments: "Bağlantılı çalgılar", officialLanguage: "Dünya resmi dili sayfası" },
    play: { ...enText.play, active: "aktif", ready: "hazır", quickStart: "Hızlı başlangıç", steps: ["Sesi aç", "Bir çalgı seç", "Tıkla, sürükle veya klavyeyle çal"], audioLocked: "Ses kilitli", mobileDrawerTitle: "Ses DNA'sı, kültürel notlar ve tuşlar" },
    seo: { title: "Timbroam Türkçe", description: "Geleneksel çalgıları, akortları, varyantları ve kültürel bağlamı tarayıcıda keşfedin." }
  },
  fa: {
    ...enText,
    brandTagline: "محیط اجرای سازهای جهان",
    nav: { play: "نواختن", instruments: "سازها", about: "درباره", qa: "QA", audioLab: "آزمایشگاه صدا" },
    controls: { instrument: "ساز", variant: "گونه", tuning: "کوک", language: "زبان", approxTuning: "کوک تقریبی", reviewedTuning: "کوک بازبینی شده" },
    pages: { ...enText.pages, instrumentsEyebrow: "فهرست قابل اجرا", instrumentsTitle: "فهرست سازهای جهان", origin: "خاستگاه", family: "خانواده", openInstrument: "باز کردن ساز", localeFocus: "صفحه زبان", connectedInstruments: "سازهای مرتبط", officialLanguage: "صفحه زبان جهانی رسمی" },
    play: { ...enText.play, active: "فعال", ready: "آماده", quickStart: "شروع سریع", steps: ["صدا را فعال کنید", "یک ساز انتخاب کنید", "کلیک، کشیدن یا کیبورد را به کار ببرید"], audioLocked: "صدا قفل است", mobileDrawerTitle: "دی ان ای صدا، یادداشت فرهنگی و کلیدها" },
    seo: { title: "Timbroam فارسی", description: "سازهای سنتی، کوک ها، گونه ها و زمینه فرهنگی را در مرورگر اجرا و بررسی کنید." }
  },
  hi: {
    ...enText,
    brandTagline: "विश्व वाद्य यंत्र खेल-स्थल",
    nav: { play: "बजाएं", instruments: "वाद्य", about: "परिचय", qa: "QA", audioLab: "ऑडियो लैब" },
    controls: { instrument: "वाद्य", variant: "रूप", tuning: "स्वर", language: "भाषा", approxTuning: "अनुमानित स्वर", reviewedTuning: "समीक्षित स्वर" },
    pages: { ...enText.pages, instrumentsEyebrow: "बजाने योग्य सूची", instrumentsTitle: "विश्व वाद्य सूची", origin: "उत्पत्ति", family: "परिवार", openInstrument: "वाद्य खोलें", localeFocus: "भाषा पृष्ठ", connectedInstruments: "संबंधित वाद्य", officialLanguage: "विश्व आधिकारिक भाषा पृष्ठ" },
    play: { ...enText.play, active: "सक्रिय", ready: "तैयार", quickStart: "त्वरित आरंभ", steps: ["ऑडियो चालू करें", "वाद्य चुनें", "क्लिक, ड्रैग या कीबोर्ड से बजाएं"], audioLocked: "ऑडियो लॉक है", mobileDrawerTitle: "ध्वनि DNA, सांस्कृतिक नोट और कुंजियां" },
    seo: { title: "Timbroam हिन्दी", description: "ब्राउज़र में पारंपरिक वाद्य, स्वर, रूप और सांस्कृतिक संदर्भ बजाएं और खोजें।" }
  },
  sn: {
    ...enText,
    brandTagline: "nzvimbo yekuridza zviridzwa zvepasi",
    nav: { play: "Ridza", instruments: "Zviridzwa", about: "Nezvazvo", qa: "QA", audioLab: "Audio Lab" },
    controls: { instrument: "Chiridzwa", variant: "Rudzi", tuning: "Kugadzirisa", language: "Mutauro", approxTuning: "Kugadzirisa kwefungidziro", reviewedTuning: "Kugadzirisa kwakaongororwa" },
    pages: { ...enText.pages, instrumentsEyebrow: "Katalogi inoridzwa", instrumentsTitle: "Katalogi yezviridzwa zvepasi", origin: "Kwakatangira", family: "Mhuri", openInstrument: "Vhura chiridzwa", localeFocus: "Peji remutauro", connectedInstruments: "Zviridzwa zvakabatana", officialLanguage: "Peji remutauro wepasi" },
    play: { ...enText.play, active: "chiri kushanda", ready: "chagadzirira", quickStart: "Tanga nokukurumidza", steps: ["Batidza odhiyo", "Sarudza chiridzwa", "Dzvanya, dhonza kana shandisa keyboard"], audioLocked: "Odhiyo yakakiyiwa", mobileDrawerTitle: "Sound DNA, tsika, uye makiyi" },
    seo: { title: "Timbroam ChiShona", description: "Ridza uye ongorora zviridzwa zvechinyakare, kugadzirisa, marudzi uye tsika mubrowser." }
  },
  id: {
    ...enText,
    brandTagline: "ruang bermain instrumen dunia",
    nav: { play: "Main", instruments: "Instrumen", about: "Tentang", qa: "QA", audioLab: "Lab Audio" },
    controls: { instrument: "Instrumen", variant: "Varian", tuning: "Laras", language: "Bahasa", approxTuning: "Laras perkiraan", reviewedTuning: "Laras tertinjau" },
    pages: { ...enText.pages, instrumentsEyebrow: "Katalog yang dapat dimainkan", instrumentsTitle: "Katalog Instrumen Dunia", origin: "Asal", family: "Keluarga", openInstrument: "Buka instrumen", localeFocus: "Halaman bahasa", connectedInstruments: "Instrumen terkait", officialLanguage: "Halaman bahasa dunia resmi" },
    play: { ...enText.play, active: "aktif", ready: "siap", quickStart: "Mulai cepat", steps: ["Aktifkan audio", "Pilih instrumen", "Klik, seret, atau gunakan keyboard"], audioLocked: "Audio terkunci", mobileDrawerTitle: "DNA bunyi, catatan budaya, dan tombol" },
    soundDna: { ...enText.soundDna, title: "DNA bunyi", detailsHeading: "Keluarga / artikulasi / resonansi", variantInfo: "Info varian", construction: "Konstruksi", controls: "Kontrol", samples: "Sampel", tuningWarning: "Peringatan laras", tuning: "Laras", respectNote: "Catatan hormat", culturalContext: "Konteks budaya", family: "Keluarga", attack: "Serangan", sustain: "Sustain", resonance: "Resonansi", ornament: "Ornamen", identity: "Identitas" },
    seo: { title: "Timbroam Bahasa Indonesia", description: "Mainkan dan jelajahi instrumen tradisional, laras, varian, dan konteks budaya di browser." }
  }
};

export interface LocalizedInstrumentCopy {
  family?: string;
  origin?: string;
  shortDescription?: string;
  culturalNote?: string;
  culturalCaution?: string;
}

export const instrumentCopyByLocale: Partial<Record<LocaleCode, Partial<Record<InstrumentId, LocalizedInstrumentCopy>>>> = {
  kr: {
    gayageum: {
      family: "뜯는 현악기",
      origin: "한국",
      shortDescription: "가야금은 줄 재료, 안족 배치, 왼손 농현이 소리를 크게 좌우하는 한국의 뜯는 현악기입니다.",
      culturalNote: "산조, 풍류/법금, 현대 확장 악기는 서로 다른 연행 맥락으로 다루어야 합니다."
    },
    daegeum: {
      family: "숨으로 부는 가로피리",
      origin: "한국",
      shortDescription: "대금은 취구, 지공, 청공과 갈대 청의 떨림이 음색을 만드는 한국 대나무 피리입니다.",
      culturalNote: "청의 장력과 노출, 요성/퇴성 표현은 단순 플루트 효과가 아니라 대금 정체성의 일부입니다."
    },
    janggu: {
      family: "모래시계형 장구",
      origin: "한국",
      shortDescription: "장구는 북편과 채편의 대비, 장단, 타격 위치가 함께 작동하는 한국 장단 악기입니다.",
      culturalNote: "장단 이름과 타법은 지역과 맥락을 지우지 않도록 조심스럽게 표시해야 합니다."
    }
  },
  zh: {
    erhu: {
      family: "拉弦乐器",
      origin: "中国",
      shortDescription: "二胡是两根弦的拉弦乐器，没有指板，琴筒膜面和揉弦控制塑造其歌唱性音色。",
      culturalNote: "蟒皮膜面的材料和证书语境需要清楚呈现。"
    }
  },
  fr: {
    kora: {
      family: "Harpe-luth pincée",
      origin: "Lignées jali/griot mandingues en Afrique de l'Ouest",
      shortDescription:
        "La kora est une harpe-luth liée aux lignées jali/griot, aux motifs imbriqués et au récit musical.",
      culturalNote:
        "Le contexte jali/griot doit rester visible et ne pas être réduit à un simple son ouest-africain générique."
    }
  },
  ar: {
    oud: {
      family: "عود وتري مقطوف بلا دساتين",
      origin: "الشرق الأوسط وشمال أفريقيا وتركيا ومناطق مجاورة",
      shortDescription: "العود آلة وترية كمثرية بلا دساتين، تتغير هويتها الصوتية مع المقام والمنطقة وعدد الأوتار.",
      culturalNote: "ينبغي عرض المقام والفروق الإقليمية بوصفها سياقات أداء لا زخارف عامة."
    },
    ney: {
      family: "ناي نفخي طرفي",
      origin: "ممارسات موسيقية شرق أوسطية وتركية وفارسية",
      shortDescription: "الناي آلة نفخ طرفية تعتمد على النفس، زاوية الفم، والانتقالات الدقيقة بين الطبقات.",
      culturalNote: "يجب التفريق بين تقاليد الناي العربية والتركية والفارسية عند عرض النسخ."
    }
  },
  tr: {
    oud: {
      family: "Perdesiz mızraplı lavta",
      origin: "Orta Doğu, Kuzey Afrika, Türkiye ve komşu bölgeler",
      shortDescription: "Ud, makam ve bölgesel icra bağlamına göre değişen perdesiz, armudi gövdeli bir lavtadır.",
      culturalNote: "Makam ve bölgesel farklar yüzeysel etiket değil, çalgının kullanım bağlamıdır."
    },
    ney: {
      family: "Uçtan üflemeli kamış flüt",
      origin: "Orta Doğu, Türk ve Fars müzik pratikleri",
      shortDescription: "Neyde nefes kenarı, mikro büküm ve üfleme basıncı ses kimliğini belirler.",
      culturalNote: "Türk, Arap ve Fars ney gelenekleri ayrı bağlamlarla sunulmalıdır."
    }
  },
  fa: {
    ney: {
      family: "نی انتهایی",
      origin: "زمینه های موسیقی خاورمیانه، ترکی و ایرانی",
      shortDescription: "نی با نفس، زاویه دهان و تغییرات ظریف کوک هویت صوتی خود را می سازد.",
      culturalNote: "گونه های ایرانی، عربی و ترکی نی باید در زمینه اجرایی جداگانه نمایش داده شوند."
    }
  },
  hi: {
    sitar: {
      family: "झंकारयुक्त तार वाद्य",
      origin: "उत्तर भारतीय शास्त्रीय और संबंधित परंपराएं",
      shortDescription: "सितार में मींड, चिकारी और सहानुभूति तार उसके स्वर और राग संदर्भ को आकार देते हैं।",
      culturalNote: "राग, ड्रोन और मींड सीमाओं को सिर्फ सजावट नहीं बल्कि वादन-संदर्भ मानना चाहिए।"
    },
    tabla: {
      family: "झिल्लीदार ताल वाद्य जोड़ी",
      origin: "दक्षिण एशिया",
      shortDescription: "तबला दायां और बायां की जोड़ी है, जहां बोल, स्याही क्षेत्र और बायां दबाव स्वर बदलते हैं।",
      culturalNote: "बोल और घराना-संदर्भ को सामान्य ड्रम लेबल में समेटना ठीक नहीं है।"
    }
  },
  sn: {
    mbira: {
      family: "Lamellophone",
      origin: "Zimbabwe uye mamiriro emimhanzi yeShona",
      shortDescription: "Mbira ine matine anoridzwa nemaoko maviri, kurira kwebuzz, uye mapatani anopindirana.",
      culturalNote: "Chimiro cheShona, mabasa emunharaunda, uye maitiro emaoko zvinofanira kuramba zvichionekwa."
    }
  },
  id: {
    gamelan_saron: {
      family: "Idiofon perunggu",
      origin: "Ansambel gamelan Indonesia",
      shortDescription:
        "Saron adalah instrumen bilah perunggu dalam gamelan, dengan laras ansambel, ombak, dan teknik meredam.",
      culturalNote: "Laras gamelan bergantung pada ansambel; slendro atau pelog generik harus tetap diberi peringatan."
    }
  }
};

export function isLocaleCode(value: string | undefined): value is LocaleCode {
  return localeCodes.includes(value as LocaleCode);
}

export function getLocaleFromPath(pathname: string): LocaleCode {
  const firstSegment = pathname.split("/").filter(Boolean)[0];

  return isLocaleCode(firstSegment) ? firstSegment : defaultLocale;
}

export function stripLocalePrefix(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);

  if (isLocaleCode(segments[0])) {
    const stripped = `/${segments.slice(1).join("/")}`;
    return stripped === "/" ? "/" : stripped.replace(/\/$/, "") || "/";
  }

  return pathname || "/";
}

export function localizePath(locale: LocaleCode, pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const cleanPath = normalized === "" ? "/" : normalized;

  if (locale === defaultLocale) {
    return cleanPath;
  }

  return cleanPath === "/" ? `/${locale}` : `/${locale}${cleanPath}`;
}

export function getLocaleConfig(locale: LocaleCode): LocaleConfig {
  return localeConfigs[locale];
}

export function getUiText(locale: LocaleCode): UiText {
  return uiTextByLocale[locale];
}

export function getInstrumentLocales(instrumentId: string): LocaleCode[] {
  return instrumentLocaleMap[instrumentId as InstrumentId] ?? [];
}
